/**
 * Customer-facing purchase troubleshooting.
 * ---------------------------------------------------------------------------
 * If the UI lags behind the network (slow RPC, closed tab, wallet timeout) the
 * buyer can paste their transaction signature and verify it directly on-chain.
 * The lookup runs server-side through `confirmReceipt`, so the answer reflects
 * the real network state, not client-side optimism.
 */
import { useEffect, useRef, useState } from "react";
import { AlertCircle, CheckCircle2, Loader2, ArrowUpRight, LifeBuoy } from "lucide-react";
import { explorerTxUrl } from "@/lib/treasury";
import { track } from "@/lib/analytics";

type Result = {
  verified: boolean;
  unreachable: boolean;
  status: string;
  receiptId: string;
  signature: string;
};

export function TxTroubleshootModal({
  open,
  onClose,
  initialSignature = "",
}: {
  open: boolean;
  onClose: () => void;
  initialSignature?: string;
}) {
  const [signature, setSignature] = useState(initialSignature);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<Result | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setSignature(initialSignature);
    setResult(null);
    setError(null);
    const id = window.setTimeout(() => inputRef.current?.focus(), 40);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(id);
      window.removeEventListener("keydown", onKey);
    };
  }, [open, initialSignature, onClose]);

  if (!open) return null;

  const check = async () => {
    const sig = signature.trim();
    if (!sig) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const { confirmReceipt } = await import("@/lib/receipt.functions");
      const res = (await confirmReceipt({ data: { signature: sig } })) as Result;
      setResult(res);
      track("tx_lookup", { verified: res.verified, status: res.status });
    } catch {
      setError("Lookup failed. Please try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[120] flex items-end justify-center bg-black/70 p-0 backdrop-blur-sm sm:items-center sm:p-6"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="tx-help-title"
        data-testid="tx-troubleshoot-modal"
        className="w-full max-w-lg rounded-t-3xl border border-white/10 bg-background p-6 sm:rounded-3xl"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 id="tx-help-title" className="text-base font-bold tracking-tight">
              Transaction help
            </h2>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              Payment sent but no receipt shown? Paste your transaction signature to verify it
              directly on Solana.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close transaction help"
            className="shrink-0 rounded-full border border-white/10 px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
          >
            ✕
          </button>
        </div>

        <label htmlFor="tx-help-sig" className="mt-5 block text-xs font-semibold">
          Transaction signature
        </label>
        <input
          id="tx-help-sig"
          ref={inputRef}
          value={signature}
          onChange={(e) => setSignature(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void check();
          }}
          spellCheck={false}
          placeholder="5f3K…"
          className="mt-1.5 w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2.5 font-mono text-xs outline-none focus-visible:ring-2 focus-visible:ring-saffron"
        />

        <button
          type="button"
          onClick={() => void check()}
          disabled={busy || signature.trim().length === 0}
          data-testid="tx-check-onchain"
          className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-saffron px-4 py-2.5 text-sm font-bold text-obsidian transition-opacity disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
          Check transaction status on-chain
        </button>

        <div aria-live="polite" className="mt-4 text-xs leading-relaxed">
          {error && <p className="text-destructive">{error}</p>}
          {result && (
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
              <p className="flex items-center gap-2 font-semibold">
                {result.verified ? (
                  <CheckCircle2 className="h-4 w-4 text-primary" aria-hidden />
                ) : (
                  <AlertCircle className="h-4 w-4 text-muted-foreground" aria-hidden />
                )}
                {result.verified
                  ? "Confirmed on-chain — your purchase is settled."
                  : result.unreachable
                    ? "Network unreachable right now — try again shortly."
                    : result.status === "invalid"
                      ? "That does not look like a Solana signature."
                      : `Not confirmed yet (status: ${result.status}).`}
              </p>
              <p className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                Receipt ID {result.receiptId}
              </p>
              <a
                href={explorerTxUrl(result.signature)}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.16em] underline-offset-2 hover:underline"
              >
                View on Solscan <ArrowUpRight className="h-3 w-3" aria-hidden />
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Small inline trigger placed next to the purchase buttons. */
export function TxTroubleshootTrigger({ signature }: { signature?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="tx-troubleshoot-trigger"
        className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
      >
        <LifeBuoy className="h-3.5 w-3.5" aria-hidden />
        Payment sent but no receipt? Check status on-chain
      </button>
      <TxTroubleshootModal
        open={open}
        onClose={() => setOpen(false)}
        initialSignature={signature ?? ""}
      />
    </>
  );
}
